import React from 'react';
import { cn } from '@/lib/utils';
import { MapPin, Clock, MessageCircle, Coins, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Message, ProposalContextMetadata } from '@/types/chat';

interface ProposalContextMessageProps {
  message: Message;
  isFromUser: boolean;
}

const ProposalContextMessage: React.FC<ProposalContextMessageProps> = ({
  message,
  isFromUser
}) => {
  const metadata = message.metadata as ProposalContextMetadata;
  const formattedTime = new Date(message.sent_at).toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  const urgencyMap = {
    'urgently': 'Urgently (within 24 hours)',
    'soon': 'Soon (within a few days)', 
    'in_the_week': 'This week',
    'flexible': 'Flexible timing'
  };

  const urgencyColors = {
    'urgently': 'bg-red-100 text-red-800 border-red-200',
    'soon': 'bg-orange-100 text-orange-800 border-orange-200',
    'in_the_week': 'bg-blue-100 text-blue-800 border-blue-200',
    'flexible': 'bg-green-100 text-green-800 border-green-200'
  };

  return (
    <div 
      className={cn(
        "flex flex-col max-w-[85%]",
        isFromUser ? "ml-auto items-end" : "mr-auto items-start"
      )}
    >
      <Card className={cn(
        "w-full shadow-md border",
        isFromUser 
          ? "bg-muted border-border" 
          : "bg-white border-primary/20"
      )}>
        <CardContent className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-primary" />
            <h4 className="font-semibold text-sm text-gray-900">Service Request Inquiry</h4>
          </div>

          {/* Service Request Details */}
          <div className="space-y-3">
            <div>
              <h5 className="text-xs font-medium text-gray-600 mb-1">Original Request:</h5>
              <p className="text-sm text-gray-900 italic">
                "{metadata.serviceRequest.originalRequest}"
              </p>
            </div>

            {/* Location and Timeline */}
            <div className="flex flex-wrap gap-2">
              {metadata.serviceRequest.town && (
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <MapPin className="w-3 h-3" />
                  <span>{metadata.serviceRequest.town}</span>
                </div>
              )}
              
              {metadata.serviceRequest.urgency && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-xs border",
                    urgencyColors[metadata.serviceRequest.urgency as keyof typeof urgencyColors] || 
                    "bg-gray-100 text-gray-800 border-gray-200"
                  )}
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {urgencyMap[metadata.serviceRequest.urgency as keyof typeof urgencyMap] || metadata.serviceRequest.urgency}
                </Badge>
              )}
            </div>
          </div>

          {/* Proposal Details */}
          <div className="border-t pt-3">
            <h5 className="text-xs font-medium text-gray-600 mb-2">Business Proposal</h5>
            
            <div className="space-y-2">
              {/* Quote - New flexible system or deprecated field */}
              {(metadata.quote || metadata.proposal.quote) && (
                <div className="space-y-2">
                  {metadata.quote ? (
                    // New flexible quote system
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Coins className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-medium text-green-800">
                            {metadata.quote.status === 'complete' ? 'Complete Quote' : 'Partial Quote'}
                          </span>
                        </div>
                        {/* Price display based on type */}
                        <div className="text-sm font-semibold text-green-700">
                          {metadata.quote.priceType === 'fixed' && metadata.quote.fixedPrice && (
                            <>£{metadata.quote.fixedPrice.toFixed(2)}</>
                          )}
                          {metadata.quote.priceType === 'estimate' && metadata.quote.estimatedPrice && (
                            <>~£{metadata.quote.estimatedPrice.toFixed(2)}</>
                          )}
                          {metadata.quote.priceType === 'range' && metadata.quote.minPrice && metadata.quote.maxPrice && (
                            <>£{metadata.quote.minPrice.toFixed(2)} - £{metadata.quote.maxPrice.toFixed(2)}</>
                          )}
                        </div>
                      </div>
                      
                      {/* Quote items if available */}
                      {metadata.quote.items && metadata.quote.items.length > 0 && (
                        <div className="space-y-1 text-xs">
                          {metadata.quote.items.map((item, index) => (
                            <div key={item.id} className="flex justify-between items-center py-1">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="text-gray-600">{item.label}</span>
                                <span className="text-gray-400">
                                  ({item.quantity} {item.unitLabel})
                                </span>
                              </div>
                              <span className="text-gray-700 font-medium">
                                £{item.lineTotal.toFixed(2)}
                              </span>
                            </div>
                          ))}
                          {metadata.quote.items.length > 1 && (
                            <div className="border-t border-green-200 pt-1 mt-1">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 font-medium">Total</span>
                                <span className="text-green-700 font-semibold">
                                  £{metadata.quote.items.reduce((sum, item) => sum + item.lineTotal, 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Quote notes if available */}
                      {metadata.quote.notes && (
                        <div className="mt-2 pt-2 border-t border-green-200">
                          <p className="text-xs text-gray-600 italic">{metadata.quote.notes}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Fallback to deprecated quote field
                    <div className="flex items-center gap-2">
                      <Coins className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">
                        £{metadata.proposal.quote}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Response Message */}
              {metadata.proposal.responseMessage && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <MessageCircle className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {metadata.proposal.responseMessage}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Business Attribution */}
          <div className="border-t pt-3 flex items-center justify-between">
            <div className="text-xs text-gray-600">
              From: <span className="font-medium">{metadata.business.name}</span>
            </div>
            <div className={cn(
              "text-xs",
              isFromUser ? "text-secondary-foreground/70" : "text-gray-500"
            )}>
              {formattedTime}
            </div>
          </div>

          {/* Additional Context Message */}
          {message.content && (
            <div className="border-t pt-3">
              <p className="text-sm text-gray-700">{message.content}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProposalContextMessage;